import { Injectable } from '@angular/core';

import { Platform } from 'ionic-angular';
import { APP_CONFIG, Region } from '../../environment';
import { Branding } from '../../environment/branding';
import { ICustomer } from '../../model/models/customer/customer.interface';
import { ICustomerEntity } from '../../model/api/customer/customer.entity.interface';
import { CustomerMapper } from '../customer/customer.mapper';
import { IResponse } from '../../model/api/response.entity.interface';
import { HttpProvider } from '../http/http';
import { CountryDeclarationProvider } from '../country-declaration/country-declaration';
import { OrderMapper } from '../order/order.mapper';
import { IOrderSummary } from '../../model/models/order/order-summary-interface';
import { FormControlProvider } from '../formbuilder/formcontrol';
import { StorageProvider } from '../storage/storage';
import { TranslateService } from '@ngx-translate/core';
import { StorageType } from '../../model/types/storage-type';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { OnfidoProvider } from '../../providers/myria/onfido-provider';
import { RouteProvider } from '../../providers/route/route';
import { ResourceProvider } from '../../providers/resource/resource';
import { SegmentProvider } from '../../providers/analytics/segment';
import { LoginProvider } from '../myria/login-provider';
import { IterableProvider, NotificationChannels } from '../../providers/marketing/iterable';
import { KYC_ATTEMPT_STATUS, KYC_STATUS} from '../../providers/myria/app-variables';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { SocialProvider } from '../../providers/social/social';
import { BehaviorSubject } from 'rxjs';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { CustomerIdentificationProcessorType } from '../../model/types/customer-identification-processor-type';
import { ICustomerIdentificationProcessor } from '../../model/models/customer/customer.identification.interface';
import { CustomerIdentificationStatusType } from '../../model/types/customer-identification-status-type';
import { CustomersProvider } from '../../providers/myria/customer-provider/customers-provider';
import { AnalyticsEventType, SegmentLocationNames } from '../../model/types/analytics.const';
import { PermissionProvider } from '../myria/permission-provider';
import { injectorInstance } from '../../app/app.module';
import { DocUploadType, DocType, DocUploadStatus } from '../../model/types/doc-type';
import { DeviceProvider } from '../device/device';
import { ForeignIpGeolocationSession } from '../foreign-ip-geolocation/foreign-ip-geolocation-session';
import { GeolocationPermissionStatus } from '../../model/types/geolocation-compliance-type';

/**
 * @description Customer Account Management provider
 */
@Injectable()
export class CustomerProvider {
  initializedActivities: boolean = false;
  initPromiseActivities: Promise<any>;
  initializedCustomer: boolean = false;
  initPromiseCustomer: Promise<any> | null;

  orders: IOrderSummary[];
  customer: any;
  liteCustomer;

  customerFields: any;
  translatePromise = null;

  pendingFieldErrors: any[] = null;

  public deviceId: string;
  static addressVerificationCalled = false;

  marketingPushNotificationsOpted: BehaviorSubject<any> = new BehaviorSubject(false);
  constructor(private platform: Platform,
    private formProvider: FormControlProvider,
    private translateService: TranslateService,
    private storageProvider: StorageProvider,
    private resourceProvider: ResourceProvider,
    private httpProvider: HttpProvider,
    private loginProvider: LoginProvider,
    private analytics: SegmentProvider,
    private iterableProvider: IterableProvider,
    private utilsProvider: UtilsProvider,
    private socialProvider: SocialProvider,
    private routeProvider: RouteProvider,
    private customersProvider: CustomersProvider,
    private countryDeclarationProvider: CountryDeclarationProvider,
    private translate: TranslateService) {
    this.platform.ready().then(() => {
      this.deviceId = LoggerProvider.uuid;
    })
  }

  private get permissionProvider(): PermissionProvider {
    return injectorInstance.get(PermissionProvider);
  }

  private get deviceProvider(): DeviceProvider {
    return injectorInstance.get(DeviceProvider);
  }

  async getAdsTracking(): Promise<boolean> {
    const customer = await this.getCustomer();
    return !!(customer && customer.adsTracking);
  }

  async setAdsTracking(value: boolean, analyticsLocation: string = 'settings notifications'): Promise<boolean> {
    const customer = await this.getCustomer();
    if (!customer) {
      throw new Error('No customer available to update ads tracking');
    }

    customer.adsTracking = value;

    this.analytics.trackEvent({
      eventName: AnalyticsEventType.ProfileUpdated,
      analyticsData: {
        location: analyticsLocation,
        email: customer.emailAddress,
        firstName: customer.firstName,
        lastName: customer.lastName,
        dateOfBirth: customer.dateOfBirth,
        phoneCountry: customer.mobilePhone ? customer.mobilePhone.countryCode : null,
        phoneNumber: customer.mobilePhone ? customer.mobilePhone.number : null,
        adsTrackingEnabled: customer.adsTracking,
      },
    });

    try {
      const result = await this.iterableProvider.updateNotificationSubscriptions(
        customer.adsTracking,
        NotificationChannels.AdvertisingInApp,
      );
      await this.storageProvider.set(
        StorageType.ConsentIDFAPermission,
        customer.adsTracking ? 'true' : 'false',
      );
      if (customer.adsTracking) {
        await this.permissionProvider.requestATTPermission();
      } else {
        await this.permissionProvider.requestATTPermissionDenied();
      }
      if (!result) {
        customer.adsTracking = !value;
        throw new Error('Iterable subscription update failed');
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.PushNotifications, 'Failed to set ads tracking');
      customer.adsTracking = !value;
      throw ex;
    }

    this.analytics.identifyCustomer(customer);
    return customer.adsTracking;
  }

  initialize(forceReload: boolean = false, page: number = 0, filter = {}, setMarketing = false, setTransactionNotification = false) {
    this.initializeCustomer(forceReload, null, setMarketing, setTransactionNotification);
  }
  // getTransactionsMyRiaCustomerProvider(page: number,filter: {} = {} ,firstTime?: boolean): Promise<IOrderSummaryEntity[]> {
  //   // if (this.filter.DateBegin) this.filter.DateBegin.setHours(12);
  //   // if (this.filter.DateEnd) this.filter.DateEnd.setHours(12);
  //   return new Promise((resolve, reject) => {
  //     this.myriaCustomersProvider.getTransactionsOrder(page, filter).subscribe(
  //       responseOrdersList => {
  //         resolve();
  //         return responseOrdersList;
  //         /*this.commonGetTransactions(page, x);*/
  //         // this.showSearchButton = !firstTime || (firstTime && this.transactions.length != 0);
  //         // if (page == 0) {
  //         //   this.emptyListMessages(firstTime);
  //         // }
  //         // this.showList = this.transactions.length != 0
  //       },
  //       err => reject(err)
  //     );
  //   });
  // }

  callNotificationSubscriptions(setMarketing: boolean = false, setTransactionNotification: boolean = false) {
    try {
      if (setMarketing) {
        this.iterableProvider.updateNotificationSubscriptions(true, NotificationChannels.MarketingEmail);
        this.iterableProvider.updateNotificationSubscriptions(true, NotificationChannels.MarketingPush);
        this.iterableProvider.updateNotificationSubscriptions(true, NotificationChannels.TransactionalPush);
      } else if (setTransactionNotification) {
        this.iterableProvider.updateNotificationSubscriptions(true, NotificationChannels.TransactionalPush);
      }
      this.setAdsTrackingSubscription();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, "Notification permission failed to update");
    }
  }

  async setMarketingNotifications(): Promise<any> {
    await this.iterableProvider.updateNotificationSubscriptions(true, NotificationChannels.MarketingEmail);
    await this.iterableProvider.updateNotificationSubscriptions(true, NotificationChannels.MarketingPush);
    return null;
  }

  async setAdsTrackingSubscription(): Promise<any> {
    const consentIDFAPermission = await this.storageProvider.get(StorageType.ConsentIDFAPermission);
    if (!this.customer || typeof this.customer.adsTracking !== 'boolean') {
      return;
    }

    if (this.customer.adsTracking && consentIDFAPermission !== 'true') {
      await this.iterableProvider.updateNotificationSubscriptions(false, NotificationChannels.AdvertisingInApp);
    } else if (!this.customer.adsTracking && consentIDFAPermission === 'true') {
      await this.iterableProvider.updateNotificationSubscriptions(true, NotificationChannels.AdvertisingInApp);
      this.customer.adsTracking = true;
    }
  }

  initializeCustomer(forceReload: boolean = false, initResolve = null, setMarketing = false, setTransactionNotification = false) {
    if (this.initializedCustomer && !forceReload)
      return;
    else if (this.initPromiseCustomer && !initResolve && !forceReload)
      return;

    // protection in the case lite users would accidentely land here
    if (this.httpProvider.getAuthIsLiteUser()) {
      this.customer = {};
      return;
    }

    if (!this.initPromiseCustomer)
      this.initPromiseCustomer = new Promise((resolve) => {
        if (!initResolve)
          initResolve = resolve;
      });

    if(forceReload || !this.initializedCustomer){
      const profileRequest = this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}profile`, {}, null, this.getProfileDeviceHeaders());
      this.primeGeolocationCache();
      profileRequest.then(async (response) => {
        this.initPromiseCustomer = null;
        this.initializedCustomer = true;
        this.customer = CustomerMapper.fromCustomerEntity(response);

        if (initResolve)
          initResolve();

        // identifyCustomer through the loginProvider to provide the user details IF available (try to avoid unnecessary calls to the user endpoint just for additional identify traits since the data shouldn't change often)
        // REMEMBER: once identify traits have been supplied we don't have to resupply them until they have actually changed
        this.loginProvider.identifyCustomer(this.customer);

        // The Identify call creates an account in iterable, to update the subscription we need to give time otherwise the notification subscription returns error.
        window.setTimeout(() => {
          this.callNotificationSubscriptions(setMarketing, setTransactionNotification);
        }, 10000);


        return true;
      }, (err) => {
        if (err.status === 401 || err.status === 403 || err.status === 404) // these are already handled and shouldn't cause a reload
          this.initPromiseCustomer = null;
        else
          window.setTimeout(() => {
            this.initializeCustomer(true, initResolve);
          }, 10000);
      });
    }
  }

  getProfileDeviceHeaders(): { [header: string]: string } | null {
    const headers: { [header: string]: string } = {};
    const timeZone = DeviceProvider.getDeviceTimeZone();
    if (timeZone) {
      headers.DeviceTimeZone = timeZone;
    }
    const fix = ForeignIpGeolocationSession.getRecentFix();
    if (fix) {
      headers.DeviceLatitude = String(fix.latitude);
      headers.DeviceLongitude = String(fix.longitude);
    }
    return Object.keys(headers).length > 0 ? headers : null;
  }

  primeGeolocationCache(): void {
    const alreadyHaveFix = !!ForeignIpGeolocationSession.getRecentFix() || !!ForeignIpGeolocationSession.pendingFix;
    if (!this.platform.is('cordova') || alreadyHaveFix) {
      return;
    }
    this.permissionProvider.getLocationAuthorizationStatus()
      .then(async (status) => {
        if (status !== GeolocationPermissionStatus.Granted) {
          return;
        }
        const fix = await ForeignIpGeolocationSession.trackPendingFix(this.deviceProvider.getGeolocation());
        if (!fix) {
          LoggerProvider.warning(this, LoggerEvent.CordovaGeolocation, 'Location permission granted but no GPS fix at session open; /profile keeps the time zone only');
        } else if (fix.isMock && this.platform.is('android')) {
          LoggerProvider.warning(this, LoggerEvent.CordovaGeolocation, 'Mock GPS fix at session open; coordinates withheld from /profile');
        }
      })
      .catch((ex) => LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Background GPS read for /profile failed'));
  }

  logout() {
    ForeignIpGeolocationSession.resetOnLogout();
    this.initializedCustomer = false;
    this.initPromiseCustomer = null;
    this.customersProvider.transactionsCondensed = null;
    this.customersProvider.transactionsv2 = null;
    this.customersProvider.transactions = null;
    this.customersProvider.transactionsInfo = null;
    this.customer = null;
  }

  ensureInitializedCustomer() {
    this.initializeCustomer();
    if (this.initPromiseCustomer)
      return this.initPromiseCustomer;
    return null;
  }

  async getCustomer(forceReload: boolean = false): Promise<ICustomer> {
    if (forceReload)
      this.initializeCustomer(true);
    await this.ensureInitializedCustomer();
    return this.customer;
  }

  // TODO !!!!!!!!!!!!!!!!!! OBSOLETE !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
  async isCustomerIDInvalid() {
    const customer = await this.getCustomer(true);
    const today = new Date();
    // Users verified by Signicat do not populate our identification data object
    const isSignicatVerified = await this.getIdentityVerificationFromCustomer(CustomerIdentificationProcessorType.Signicat)
    if (!isSignicatVerified && customer.identification.details && customer.identification.details.expirationDate) {
      if (new Date(customer.identification.details.expirationDate.toString()).getTime() < new Date(today).getTime())
      return true;
    }
    return false;
  }

  hasCustomer() {
    if (this.initializedCustomer || this.initPromiseCustomer)
      return true;
    return false;
  }

  getStates() {
    return this.formProvider.getFieldsCustomer().then((fields) => {
      const state = fields.filter((field) => (field.id === 'state'));
      if (state && state.length > 0)
        return state[0].options;
      return null;
    });
  }

  updateAdditionalDetails(model: any): Promise<ICustomer> {
    const payload = CustomerMapper.toAdditionalFieldsEntity(model);
    if (!payload.fields || payload.fields.length === 0) {
      return Promise.resolve(this.customer);
    }
    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}customer-profile/additionalfields`, payload, (res: ICustomerEntity) => {
      return CustomerMapper.fromCustomerEntity(res);
    });
  }

  updateCustomer(customer: ICustomer): Promise<ICustomer> {
    const entity = CustomerMapper.toCustomerEntity(customer);
    return this.httpProvider
      .put(`${APP_CONFIG.remittanceApiBaseUrl}customer-profile`, entity, (res: IResponse) => {
        const customer = CustomerMapper.fromCustomerEntity(<ICustomerEntity>res.model);
        if (customer)
          this.customer = customer;
        return customer;
      });
  }

  updateEmail(customer: ICustomer): Promise<ICustomer> {
    const entity = CustomerMapper.toChangeEmailEntity(customer.emailAddress);
    return this.httpProvider.put(`${APP_CONFIG.remittanceApiBaseUrl}customer-profile/email-address`, entity);
  }

  changePassword(form: any): Promise<any> {
    const formEntity = CustomerMapper.toChangePasswordEntity(form);
    return this.httpProvider.put(`${APP_CONFIG.remittanceApiBaseUrl}customer-profile/password`, formEntity);
  }

  changePasswordCst(form: any): Promise<any> {
    return this.httpProvider.post(`${APP_CONFIG.API_URL}api/Account/ChangePassword`, {
      OldPassword: form.password,
      NewPassword: form.newPassword,
      ConfirmPassword: form.newPassword,
    });
  }

  getStoredLanguage() {
    return this.storageProvider.get(StorageType.Language);
  }

  async navigateIDDetails(onfidoProvider: OnfidoProvider, openAsModal: boolean = false, analyticsLocation: string = SegmentLocationNames.Settings) {
    const fromCarousel = analyticsLocation === SegmentLocationNames.Carousel;
    try {
      const isOnfidoEnable = await onfidoProvider.isOnfidoEnable();
      const isSignicatEnabled = await this.loginProvider.hasMultipleEkycProviders() && this.loginProvider.isSignicatCountry()
      if (isOnfidoEnable && this.customer && !this.customer.identification.details) {
        const kycStatus = await this.loginProvider.getKycStatus();
        const attempt = kycStatus.attempt;
        const kycResult = kycStatus.kycResult;
        const isFirstTime = kycStatus.isFirstTime;
        onfidoProvider.setOnfidoFlow(false, true, false, fromCarousel);
        // canAccessByAttempt also counts TruNarrative, which would let an NZ customer with no Veriff attempts left start Veriff
        const canAccess = onfidoProvider.isNzEkyc()
          ? !await onfidoProvider.hasAnyZeroAttempts(true, CustomerIdentificationProcessorType.Veriff)
          : await onfidoProvider.canAccessByAttempt();

        if (isFirstTime || attempt == KYC_ATTEMPT_STATUS.VERIFIED) {
          if (!canAccess) {
            await onfidoProvider.routeExhaustedAttempts(analyticsLocation, false);
          }
          else {
            let params = {
              analyticsLocation: analyticsLocation,
              fromSettingsPage: true,
              showBack: true,
              showHelp: false,
              onSuccess: () => {
                this.loginProvider.isProfileCallNotRequired = false;
                this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
              }
            };
            if (isSignicatEnabled) {
              if (openAsModal) {
                this.routeProvider.openModal(this.routeProvider.pages.EkycMethodSelectPage, params);
              } else {
                this.routeProvider.push(this.routeProvider.pages.EkycMethodSelectPage, params);
              }
            }
            else {
              if (openAsModal) {
                this.routeProvider.openModal(onfidoProvider.getEkycInfoPage(), params);
              } else {
                this.routeProvider.push(onfidoProvider.getEkycInfoPage(), params);
              }
            }
          }
        }
        else if (attempt == KYC_ATTEMPT_STATUS.IN_PROGRESS) {
          this.routeProvider.replace(this.routeProvider.pages.OnfidoPendingPage, {
            primaryButtonAction: () => {
              this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
            }
          });
        }
        else {
          if (kycResult == KYC_STATUS.KYC_SUSPECTED) {
            onfidoProvider.openOnfidoFlow(true, SegmentLocationNames.Settings, true);
          }
          else {
            if (!canAccess) {
              await onfidoProvider.routeExhaustedAttempts(SegmentLocationNames.Settings, false);
            }
            else if (onfidoProvider.isNzEkyc()) {
              const params = {
                analyticsLocation: analyticsLocation,
                fromSettingsPage: true,
                onSuccess: () => {
                  this.loginProvider.isProfileCallNotRequired = false;
                  this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
                }
              };
              if (openAsModal)
                this.routeProvider.openModal(onfidoProvider.getEkycInfoPage(), params);
              else
                this.routeProvider.push(onfidoProvider.getEkycInfoPage(), params);
            }
            else {
              if (openAsModal) {
                this.routeProvider.openModal(this.routeProvider.pages.InfoPage, {
                  showHelp: true,
                  showBack: true,
                  img: 'assets/imgs/Warning.svg',
                  title: this.translate.instant('page.sendVerifyPage.sendVerifyFailedHeader'),
                  msg: this.translate.instant('page.sendVerifyPage.verifyFailedContent'),
                  primaryButtonText: this.translate.instant('page.sendVerifyPage.tryAgainButton'),
                  primaryButtonAction: async () => {
                    if (!canAccess) {
                      await onfidoProvider.routeExhaustedAttempts(SegmentLocationNames.Settings, false);
                    }
                    else {
                      onfidoProvider.setOnfidoFlow(false, true);
                      const params = { analyticsLocation: SegmentLocationNames.Settings, fromSettingsPage: true,
                        onSuccess: () => {
                            this.loginProvider.isProfileCallNotRequired = false;
                            this.utilsProvider.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomePage);
                          }
                       }
                      if (isSignicatEnabled)
                        this.routeProvider.replace(this.routeProvider.pages.EkycMethodSelectPage, { params: params });
                      else
                        this.routeProvider.replace(this.routeProvider.pages.OnfidoInfoPage, { params: params });
                    }
                  },
                });
              } else {
                this.routeProvider.push(this.routeProvider.pages.InfoPage, {
                  showHelp: true,
                  showBack: true,
                  img: 'assets/imgs/Warning.svg',
                  title: this.translate.instant('page.sendVerifyPage.sendVerifyFailedHeader'),
                  msg: this.translate.instant('page.sendVerifyPage.verifyFailedContent'),
                  primaryButtonText: this.translate.instant('page.sendVerifyPage.tryAgainButton'),
                  primaryButtonAction: async () => {
                    if (!canAccess) {
                      await onfidoProvider.routeExhaustedAttempts(SegmentLocationNames.Settings, false);
                    }
                    else {
                      onfidoProvider.setOnfidoFlow(false, true);
                      const params = { analyticsLocation: SegmentLocationNames.Settings, fromSettingsPage: true,
                        onSuccess: () => {
                            this.loginProvider.isProfileCallNotRequired = false;
                            this.routeProvider.setRoot(this.routeProvider.pages.TabsHomePage);
                          }
                       }
                      if (isSignicatEnabled)
                        this.routeProvider.replace(this.routeProvider.pages.EkycMethodSelectPage, { params: params });
                      else
                        this.routeProvider.replace(this.routeProvider.pages.OnfidoInfoPage, { params: params });
                    }
                  },
                });
              }
            }
          }
        }
      } else {
        if (openAsModal) {
          this.routeProvider.openModal(this.routeProvider.pages.SettingsIdPage, { fromCarousel });
        } else {
          this.routeProvider.push(this.routeProvider.pages.SettingsIdPage, { fromCarousel });
        }
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to navigate ID details');
    }
  }

  async setLanguage(lang, save: boolean = true, reset: boolean = true, force: boolean = false) {

    let languageCultures = this.utilsProvider.availableLanguageCultures();
    let value = languageCultures.find(langCult => langCult === lang);
    if (!value)
      lang = this.utilsProvider.getFallbackLanguage(lang);

    let savePromise = null;
    if (save)
      savePromise = this.storageProvider.set(StorageType.Language, lang);

    if (lang === HttpProvider.currentLanguage && !force)
      return;

    LoggerProvider.info(this, 'Set language to ' + lang);
    HttpProvider.currentLanguage = lang;
    let translateLang = lang;

    await savePromise;
    this.storageProvider.set(StorageType.Language, lang); // to be on the safe side for racing conditons

    if (APP_CONFIG.branding.branding === Branding.Walmart && translateLang) {
      if (translateLang.indexOf('en-') === 0)
        translateLang = 'en-WA';
      else if (translateLang.indexOf('es-') === 0)
        translateLang = 'es-WA'
    }
    if (APP_CONFIG.featureFlags.lokaliseContent)
      translateLang = translateLang.replace('-', '_');

    // translate service seems to need a critical section to be used otherwise it might not apply a language change if already one is in progress
    if (this.translatePromise)
      await this.translatePromise;
    this.translatePromise = this.translateService.use(translateLang).toPromise();
    await this.translatePromise;
    if (lang === HttpProvider.currentLanguage && reset) {
      this.resourceProvider.initializeResources(true, true);
      this.formProvider.getFieldsRegistration(true, false, (res) =>{
        this.setCustomerFields(res);
      })
    }
  }

  getCurrentLanguage() {
    return HttpProvider.currentLanguage;
  }

  setCustomerFields(fields): void {
    this.customerFields = fields;
  }

  getCustomerFields(forceReload: boolean = false): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        let fields = this.customerFields;
        if (fields && !forceReload) resolve(fields);
        else {
          const customerFields = await this.httpProvider.get(`${APP_CONFIG.API_URL}api/Customer/MissingRequiredFields`);
          fields = customerFields;
        }
        resolve(fields);
      } catch (ex) {
        reject(ex);
      }
    });
  }

  createLiteCustomer(fields: any): Promise<any> {
    return this.httpProvider.put(`${APP_CONFIG.API_URL}api/Customer/v2`, fields, null, {
      ...this.countryDeclarationProvider.getHeaders()
    });
  }

  setLiteCustomerFieldsValues(liteCustomer) {
    this.liteCustomer = liteCustomer;
  }

  getLiteCustomerFieldsValues() {
    return this.liteCustomer;
  }

  async saveLiteCustomerFields() {
    const liteCustomerFields = await this.httpProvider.get(`${APP_CONFIG.API_URL}api/Customer/NewCustomerRequiredFields`);
    UtilsProvider.updatePostalCodeRegex(liteCustomerFields);
    this.setCustomerFields(liteCustomerFields);
  }

  setMarketingPushNotificationsOpted(marketingPushNotificationsOpted: boolean) {
    this.marketingPushNotificationsOpted.next(marketingPushNotificationsOpted);
  }

  isMarketingPushNotificationsOpted() {
    return this.marketingPushNotificationsOpted.value;
  }

  async navigatePhoneVerification(onContinue) {
    try {
      const customer = await this.getCustomer();
      const { countryCode, mobileNumber } = this.getMobileFromattedNumber(customer);
      const params = {
        phone: { phoneNumber: mobileNumber, countryCode: countryCode },
        onBackCallback: () => {
          this.routeProvider.popTo(this.routeProvider.pages.SendSummaryPage);
        },
        onContinue: onContinue,
        phoneUp: true,
        fromOnfido: true,
      }

      this.routeProvider.push(this.routeProvider.pages.MobileVerifyPage, params);
    } catch (ex) {
      RmtPopupComponent.showExceptionWithLogTranslation(this, ex, "page.errorPage.phoneVerificationError");
    }
  }

  async navigateAddressVerification(onContinue, fromProfile?: boolean, preAddress?: string) {
    try {
      const customer = await this.getCustomer();
      let preSelectedAddress =  customer.address? customer.address.street : '';
      const address = preAddress ? preAddress : preSelectedAddress;
      const params = {
        onContinue: onContinue,
        fromOnfido: true,
        isStatisAddressFields: true,
        defaultAddress: address,
        fromProfile: fromProfile
      }
      this.routeProvider.openModal(this.routeProvider.pages.AddressVerifyPage, params);
    } catch (ex) {
      RmtPopupComponent.showExceptionWithLogTranslation(this, ex, "page.errorPage.addressVerificationError");
    }
  }

  // TODO !!!!!!!!!!!!!!!!!! OBSOLETE !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
  // returns true if processor is verified
  async getIdentityVerificationFromCustomer(identityVerificationType: CustomerIdentificationProcessorType | CustomerIdentificationProcessorType[]) {
    let statusVerified = false;
    try {
      const customer = await this.getCustomer();

      if (customer && customer.identification) {
        const kycProcessors = customer.identification.identityVerificationProcessors || [];
        if(typeof identityVerificationType === 'string'){
          const kycProcessorFiltered = kycProcessors.filter((obj: ICustomerIdentificationProcessor) => {
            return obj.processor === identityVerificationType;
          });
          if (kycProcessorFiltered.length > 0) {
            const status = kycProcessorFiltered[0].status;
            statusVerified = status === CustomerIdentificationStatusType.Verified;
          }
        }
        else {
          /**
           * since we are adding veriff to US users, but also according to requirement we need to also check for onfido, for users who
           * have onfido verified. Therefore in the else, we are sending an array containing verrif and onfido, then looping and checking if the user
           * identityverificationprocess contains either veriff or onfido. If user contains onfido and it is verified then we break and return from the function and same thing
           * if user contains veriff and verified then we break and return from the function.
           */
          for(let type of identityVerificationType) {
            const kycProcessorFiltered = kycProcessors.filter((obj: ICustomerIdentificationProcessor) => {
              return obj.processor === type;
            });
            if (kycProcessorFiltered.length > 0) {
              const status = kycProcessorFiltered[0].status;
              statusVerified = status === CustomerIdentificationStatusType.Verified;
              if(statusVerified) break;
            }
          }
        }
      }
    } catch (ex) {
      RmtPopupComponent.showExceptionWithLogTranslation(this, ex, "page.errorPage.KYCVerificationError");
    }

    return statusVerified;
  }

  // returns e/kyc processor if user has been verified
  async getUserVerificationProcessor() {
    const customer = await this.getCustomer();
    if (customer.identification && this.customer.identification.idVerificationStatus  && this.customer.identification.idVerificationStatus === CustomerIdentificationStatusType.Verified) {
      return customer.identification.idVerificationAuthority
   }
    return null;
  }

  getMobileFromattedNumber(customer) {
    const mobilePhone = customer.mobilePhone;
    const countryCode = (mobilePhone && mobilePhone.countryCode) ? mobilePhone.countryCode.toString() : '';
    const mobileNumber = (mobilePhone) ? mobilePhone.number : '';

    return {countryCode, mobileNumber};
  }

  getUserMobileNumber(withCountryCode: boolean = false){
    let number: string = "";
    if(this.customer && this.customer.mobilePhone){
        if(withCountryCode && this.customer.mobilePhone.countryCode) number = this.customer.mobilePhone.countryCode;
        if (this.customer.mobilePhone.number) number+= this.customer.mobilePhone.number;
    }
   return number;
  }

  ensureInitializedActivities() {
    this.initializeActivities();
    if (this.initPromiseActivities)
      return this.initPromiseActivities;
    return null;
  }

  async getOrders(forceReload: boolean = false,page:number=0,filter={}): Promise<IOrderSummary[]> {
    if (forceReload) {
      this.initializeActivities(true,page,filter);
    }
    await this.ensureInitializedActivities();
    return this.orders;
  }

  initializeActivities(forceReload: boolean = false,page:number = 0,filter={} ,initResolve = null) {
    if (this.initializedActivities && !forceReload)
      return;
    else if (this.initPromiseActivities && !initResolve)
      return;

    if (!this.initPromiseActivities)
      this.initPromiseActivities = new Promise((resolve) => {
        if (!initResolve)
          initResolve = resolve;
      });

    this.httpProvider.get(`${APP_CONFIG.remittanceApiBaseUrl}MostRecentOrders`).then(
    (responseOrdersList) => {
      this.initPromiseActivities = null;
      this.initializedActivities = true;
      this.orders = OrderMapper.fromOrderSummariesEntity(responseOrdersList.orderHistory);
      initResolve(true);
      return true;
    },
    (err) => {
      if (err.status === 401 || err.status === 403) // these are already handled and shouldn't cause a reload
        this.initPromiseActivities = null;
      else
        window.setTimeout(() => {
          this.initializeActivities(true, initResolve);
        }, 10000);
    });
  }

  hasOrders() {
    if (this.orders)
      return true;
    return false;
  }

  async isFromMalaysiaAndNonNational() {
    const customer = await this.getCustomer();
    return APP_CONFIG.region.region === Region.MY && customer.nationality !== Region.MY;
  }

  isDocUploadRequired(documentTypes) {
    return documentTypes && documentTypes.length;
  }

  isMultiDocUploadRequired(documentTypes) {
    return documentTypes &&
      documentTypes.filter(item => (item.UploadRequestStatus || item.uploadRequestStatus)  === DocUploadStatus.Open).length > 1;
  }

  getDocUploadPageForMY(documentTypes) {
    if (!documentTypes || !documentTypes.length)
      return null;

    let page = null;
    let docType = null;

    const hasVisaDoc = documentTypes.some(item => {
      const uploadDocType = item.uploadDocType || item.UploadDocType; // handle both PascalCase and camelCase scenario since this function can be called from different API
      const uploadRequestStatus = item.uploadRequestStatus || item.UploadRequestStatus;
      return uploadDocType === DocUploadType.CopyOfVisa && uploadRequestStatus === DocUploadStatus.Open;
    });

    const hasProofOfFundsDoc = documentTypes.some(item => {
      const uploadDocType = item.uploadDocType || item.UploadDocType; // handle both PascalCase and camelCase scenario since this function can be called from different API
      const uploadRequestStatus = item.uploadRequestStatus || item.UploadRequestStatus;
      return uploadDocType === DocUploadType.ProofOriginOfMoney && uploadRequestStatus === DocUploadStatus.Open;
    });


    if (hasVisaDoc && hasProofOfFundsDoc) {
      page = this.routeProvider.pages.DocUploadMYNonNationalPage;
    } else if (hasVisaDoc) {
      docType = DocUploadType.CopyOfVisa;
      page = this.routeProvider.pages.DocUploadPage;
    } else if (hasProofOfFundsDoc) {
      docType = DocUploadType.ProofOriginOfMoney;
      page = this.routeProvider.pages.DocUploadSelectPage;
    }

    return { page, docType };
  }

  navigateToDocUploadForMY(objRouteProvider, objTransaction, openModal = false, fromFinish = false, continueFinishOrder = false) {
    const { page, docType } = this.getDocUploadPageForMY(objTransaction.UploadDocumentInfo || objTransaction.uploadDocumentInfo);

    if (!page)
      return;

    const params = {
      fromFinish: fromFinish,
      transaction: objTransaction,
      continueFinishOrder: continueFinishOrder,
      ...(docType === DocUploadType.CopyOfVisa ? { docType: DocType.Visa } : {})
    };

    if (openModal)
      objRouteProvider.openModal(page, params);
    else
      objRouteProvider.replace(page, params);
  }

  isMyAndVisaRequired() {
    if(APP_CONFIG.region.region === Region.MY && ((this.customer.identification.isVisaRequired &&
      !this.customer.identification.visaDetails) || (this.customer.identification.isVisaRequired &&
        this.customer.identification.visaDetails.isExpired))) {
      return true;
    }

    return false;
  }

  emailMissingForMYNonNational() : boolean {
    return APP_CONFIG.region.region === Region.MY && this.customer &&
      !this.customer.emailAddress && this.customer.nationality !== Region.MY;
  }
}



// WEBPACK FOOTER //
// ./src/providers/customer/customer.ts